import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { GitCompare, X, ArrowRight, TrendingUp, Minus, Volume2, Square, Loader2 } from "lucide-react";
import { useSpeech } from "../hooks/useSpeech";

const LEVEL = ["Unseen", "Exposed", "Familiar", "Understands", "Can apply", "Can teach"];

const LEVEL_WORDS = ["unseen", "exposed", "familiar", "understands", "can apply", "can teach"];

function buildVerdict(a, b, shared, lvlA, lvlB, sa, sb) {
  const sameDay = new Date(a.started_at).toDateString() === new Date(b.started_at).toDateString();
  const fmt = (i) => new Date(i.started_at).toLocaleDateString(undefined, { month: "long", day: "numeric" }) + (sameDay ? ` at ${new Date(i.started_at).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}` : "");
  const parts = [sameDay ? `Comparing your two attempts on ${fmt(a).split(" at ")[0]}: the earlier one at ${fmt(a).split(" at ")[1]} and the later one at ${fmt(b).split(" at ")[1]}.` : `Comparing your ${fmt(a)} attempt with your ${fmt(b)} attempt.`];
  const improved = [], lagging = [];
  for (const c of shared) { const d = lvlB[c].after - lvlA[c].after; (d > 0 ? improved : lagging).push({ c, d, lvl: lvlB[c].after }); }
  if (improved.length) parts.push(`What improved: ${improved.map(x => `${x.c} climbed ${x.d} level${x.d > 1 ? "s" : ""} to ${LEVEL_WORDS[x.lvl]}`).join("; ")}.`);
  if (lagging.length) parts.push(`Still lagging: ${lagging.map(x => `${x.c} stayed at ${LEVEL_WORDS[x.lvl]}`).join("; ")}.`);
  if (!shared.length) parts.push("The two attempts covered different concepts, so there is no direct concept overlap to judge.");
  const gd = (sb.gaps_closed ?? 0) - (sa.gaps_closed ?? 0), gf = (sb.gaps_found ?? 0) - (sa.gaps_found ?? 0), mg = (sb.levels_gained ?? 0) - (sa.levels_gained ?? 0);
  parts.push(`Gaps found went from ${sa.gaps_found ?? 0} to ${sb.gaps_found ?? 0}${gf < 0 ? ", fewer gaps — good" : gf > 0 ? ", more gaps surfaced" : ""}; gaps closed ${gd > 0 ? "rose" : gd < 0 ? "fell" : "held"} at ${sb.gaps_closed ?? 0}; mastery gained ${mg > 0 ? "rose" : mg < 0 ? "fell" : "held"} at plus ${sb.levels_gained ?? 0}.`);
  if (b.debrief?.recommendation) parts.push(`ARIA's latest recommendation: ${b.debrief.recommendation}`);
  return parts.join(" ");
}

export function InterviewCompare({ ids, onClose }) {
  const [a, setA] = useState(null);
  const [b, setB] = useState(null);
  const { toggle, stop, speaking, loadingKey } = useSpeech();
  useEffect(() => () => stop(), []); // eslint-disable-line
  useEffect(() => {
    if (ids.length !== 2) return;
    Promise.all(ids.map(id => api.get(`/interviews/${id}`))).then(([ra, rb]) => {
      const [x, y] = [ra.data, rb.data].sort((p, q) => (p.started_at || "").localeCompare(q.started_at || ""));
      setA(x); setB(y);
    }).catch(() => {});
  }, [ids.join(",")]); // eslint-disable-line

  if (ids.length !== 2) {
    return (
      <div className="aria-card p-5 mb-6 border-dashed" data-testid="compare-picker">
        <div className="flex items-center justify-between">
          <div className="text-sm text-[var(--aria-text-muted)] inline-flex items-center gap-2"><GitCompare size={14} className="text-[var(--aria-primary)]" /> Select {2 - ids.length} more completed interview{ids.length === 1 ? "" : "s"} below to compare debriefs side by side.</div>
          <button onClick={onClose} data-testid="compare-cancel" className="text-xs text-[var(--aria-text-muted)] hover:text-[var(--aria-text)]">Cancel</button>
        </div>
      </div>
    );
  }
  if (!a || !b) return <div className="aria-card p-5 mb-6 text-sm text-[var(--aria-text-muted)]">Loading debriefs…</div>;

  const da = a.debrief || {}, dbf = b.debrief || {};
  const lvlA = Object.fromEntries((da.mastery_changes || []).map(c => [c.concept, c]));
  const lvlB = Object.fromEntries((dbf.mastery_changes || []).map(c => [c.concept, c]));
  const concepts = [...new Set([...Object.keys(lvlA), ...Object.keys(lvlB)])];
  const shared = concepts.filter(c => lvlA[c] && lvlB[c]);
  const sa = da.stats || {}, sb = dbf.stats || {};
  const fmt = (i) => new Date(i.started_at).toLocaleDateString(undefined, { month: "short", day: "numeric" });
  const verdict = buildVerdict(a, b, shared, lvlA, lvlB, sa, sb);

  const Stat = ({ label, va, vb, higherBetter = true }) => {
    const better = va === vb ? null : (higherBetter ? vb > va : vb < va);
    return (
      <div className="grid grid-cols-3 items-center text-sm py-1.5 border-b border-[var(--aria-border)]/60">
        <span className="text-[var(--aria-text-muted)] text-xs">{label}</span>
        <span className="font-mono text-center">{va ?? 0}</span>
        <span className={`font-mono text-center ${better === true ? "text-emerald-400" : better === false ? "text-amber-400" : ""}`}>{vb ?? 0}{better === true ? " ↑" : ""}</span>
      </div>
    );
  };

  return (
    <div className="aria-card p-6 mb-6 ring-glow-primary fade-in" data-testid="interview-compare">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2"><GitCompare size={15} className="text-[var(--aria-primary)]" /><span className="text-xs uppercase tracking-[0.2em] font-semibold">Compare attempts</span></div>
        <div className="flex items-center gap-2">
          <button onClick={() => toggle(verdict, "verdict")} data-testid="compare-narrate"
            className={`text-xs inline-flex items-center gap-1.5 px-2.5 py-1 rounded border ${speaking ? "border-[var(--aria-primary)] text-[var(--aria-primary)]" : "border-[var(--aria-border)] text-[var(--aria-text-muted)] hover:text-[var(--aria-text)]"}`}>
            {loadingKey === "verdict" ? <Loader2 size={12} className="animate-spin" /> : speaking ? <Square size={12} /> : <Volume2 size={12} />} {speaking ? "Stop" : "Narrate verdict"}
          </button>
          <button onClick={() => { stop(); onClose(); }} data-testid="compare-close" className="text-[var(--aria-text-muted)] hover:text-[var(--aria-text)]"><X size={16} /></button>
        </div>
      </div>
      <p className="text-sm text-[var(--aria-text-muted)] leading-relaxed mb-4 border-l-2 border-[var(--aria-primary)] pl-3" data-testid="compare-verdict">{verdict}</p>

      <div className="grid grid-cols-3 text-xs text-[var(--aria-text-muted)] mb-2">
        <span />
        <span className="text-center"><div className="font-display text-base text-[var(--aria-text)]">{fmt(a)}</div><div className="truncate px-2" data-testid="compare-a-target">{a.target}</div></span>
        <span className="text-center"><div className="font-display text-base text-[var(--aria-text)]">{fmt(b)}</div><div className="truncate px-2" data-testid="compare-b-target">{b.target}</div></span>
      </div>
      <Stat label="Questions answered" va={sa.questions_answered} vb={sb.questions_answered} />
      <Stat label="Concepts covered" va={sa.concepts_covered} vb={sb.concepts_covered} />
      <Stat label="Gaps found" va={sa.gaps_found} vb={sb.gaps_found} higherBetter={false} />
      <Stat label="Gaps closed" va={sa.gaps_closed} vb={sb.gaps_closed} />
      <Stat label="Mastery gained" va={sa.levels_gained} vb={sb.levels_gained} />

      <div className="mt-5">
        <div className="text-xs uppercase tracking-[0.2em] text-[var(--aria-text-muted)] font-semibold mb-2 inline-flex items-center gap-1.5"><TrendingUp size={12} /> Concepts across both attempts</div>
        {shared.length === 0 && <div className="text-sm text-[var(--aria-text-muted)]" data-testid="compare-no-shared">These two attempts covered different concepts — no direct overlap to compare.</div>}
        <div className="space-y-1.5">
          {shared.map(c => {
            const ca = lvlA[c], cb = lvlB[c];
            const climbed = cb.after > ca.after;
            return (
              <div key={c} className="grid grid-cols-3 items-center text-sm px-3 py-2 rounded-md bg-[var(--aria-surface-2)]" data-testid={`compare-concept-${c}`}>
                <span className="truncate">{c}</span>
                <span className="font-mono text-xs text-center text-[var(--aria-text-muted)]">{LEVEL[ca.before]} → {LEVEL[ca.after]}</span>
                <span className={`font-mono text-xs text-center inline-flex items-center justify-center gap-1 ${climbed ? "text-emerald-400" : ""}`}>{LEVEL[cb.before]} → {LEVEL[cb.after]} {climbed ? <ArrowRight size={11} /> : <Minus size={11} className="text-[var(--aria-text-muted)]" />}{climbed ? `+${cb.after - ca.after}` : ""}</span>
              </div>
            );
          })}
        </div>
        {concepts.filter(c => !shared.includes(c)).length > 0 && (
          <div className="text-xs text-[var(--aria-text-muted)] mt-3">Only in one attempt: {concepts.filter(c => !shared.includes(c)).join(", ")}</div>
        )}
      </div>

      <div className="grid sm:grid-cols-2 gap-4 mt-5">
        {[a, b].map((iv, i) => (
          <div key={iv.interview_id} className="rounded-md bg-[var(--aria-surface-2)] p-3" data-testid={`compare-summary-${i === 0 ? "a" : "b"}`}>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--aria-text-muted)] mb-1">{fmt(iv)} · summary</div>
            <p className="text-sm leading-relaxed">{iv.debrief?.summary}</p>
            {iv.debrief?.recommendation && <p className="text-xs text-[var(--aria-text-muted)] mt-2 border-l-2 border-[var(--aria-primary)] pl-2">{iv.debrief.recommendation}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}
